import { difference } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import useTracking from '@zen/utils/hooks/useTracking';

import type { TableColumnOrder } from '../types';
import { TableConfigurationTrackingAction, TableConfigurationTrackingCategory } from '../types';
import type { ColumnOrderState, StateUpdater } from '../utils/columnStateConverters';
import { resolveUpdater } from '../utils/columnStateConverters';

export const calculateColumnOrder = (initialColumns: TableColumnOrder[], savedColumnOrder: string[]) => {
  // we need to make sure any new columns are added to the order
  // say we have saved [b,c,a] and there are new columns in initial order
  // [z,a,b,c,d], then we will try to preserve the old order
  // and append new columns [b,c,a,z,d]
  // Also, if some columns get removed we want to get rid fo them from saved config
  // eg. initial ['a', 'b', 'c'], saved: ['b','c','f','a']
  // the result will be ['b','c', 'a']
  // we also need to make sure fixed columns are in the right order

  const leftFixedColumns: string[] = initialColumns.filter((column) => column.fixed === 'left').map(({ key }) => key);
  const scrollableColumns: string[] = initialColumns.filter((column) => !column.fixed).map(({ key }) => key);
  const savedScrollableColumns: string[] = savedColumnOrder.filter((columnName) => scrollableColumns.includes(columnName));
  const rightFixedColumns: string[] = initialColumns.filter((column) => column.fixed === 'right').map(({ key }) => key);

  const missingColumns: string[] = difference(scrollableColumns, savedScrollableColumns);

  return [...leftFixedColumns, ...savedScrollableColumns, ...missingColumns, ...rightFixedColumns];
};

interface ColumnOrderConfiguration {
  // Array of column keys in display order
  columnOrder: ColumnOrderState;
  // TanStack-compatible onChange handler
  onColumnOrderChange: (updater: StateUpdater<ColumnOrderState>) => void;
  // Legacy setter (kept for backward compatibility with drag-drop handlers)
  setColumnOrder: (columnOrder: ColumnOrderState) => void;
}

const STORAGE_KEY = 'table-column-order';

export const useColumnOrder = (tableId: string, initialColumns: TableColumnOrder[] = []): ColumnOrderConfiguration => {
  const [storedColumnOrder, setStoredColumnOrder] = useLocalStorage<Record<string, string[]>>(STORAGE_KEY, {});

  const { trackEvent } = useTracking();

  const initialColumnOrder: string[] = useMemo(() => initialColumns.map(({ key }) => key), [initialColumns]);
  const columnOrder: string[] = useMemo(() => {
    const customColumns: string[] = storedColumnOrder?.[tableId] || [];

    return calculateColumnOrder(initialColumns, customColumns);
  }, [initialColumns, storedColumnOrder, tableId]);

  const setColumnOrder = useCallback(
    (newColumnOrder: ColumnOrderState) => {
      trackEvent({
        category: TableConfigurationTrackingCategory,
        action: TableConfigurationTrackingAction.REORDER,
        label: tableId,
        properties: {
          tableId,
          initialColumnOrder,
          newColumnOrder
        }
      });

      setStoredColumnOrder((prev) => ({ ...prev, [tableId]: newColumnOrder }));
    },
    [tableId, trackEvent, initialColumnOrder, setStoredColumnOrder]
  );

  // TanStack-compatible onChange handler
  const onColumnOrderChange = useCallback(
    (updater: StateUpdater<ColumnOrderState>) => {
      const newOrder = resolveUpdater(updater, columnOrder);

      setColumnOrder(newOrder);
    },
    [columnOrder, setColumnOrder]
  );

  return {
    columnOrder,
    setColumnOrder,
    onColumnOrderChange
  };
};
